import L from "../../leaflet-plugins-loader";
import type {
  PositionReport,
  Dimension,
  ETA,
  ShipStaticData,
} from "@arl/leaflet-tracksymbol2";
import { INgsiAIS } from "../../../ngsild/interfaces/ngsi-ais.interface";
import { ILeafletInstance } from "../../leaflet.interface";
import { TranslateService } from "@ngx-translate/core";
import { DomUtil } from "leaflet";
import * as NgsiAIS from "../../../ngsild/models/ngsi-ais";

interface ShipType {
  name: string;
  color: string;
  fillColor: string;
}

export interface TrackerDetails {
  positionReport: PositionReport;
  marker?: L.Marker<any>;
  circle?: L.Circle<any>;
  options: {
    shipStaticData: ShipStaticData;
  };
}

const MAX_SOG_EXCLUSIVE = 102.3;

const RESERVED_COLOR = "#000000";
const RESERVED_FILL_COLOR = "#d3d3d3";
const WIG_COLOR = "#000000";
const WIG_FILL_COLOR = "#d3d3d3";
const TYPE_3X_COLOR = "#8b008b";
const TYPE_3X_FILL_COLOR = "#ff00ff";
const HSC_COLOR = "#00008b";
const HSC_FILL_COLOR = "#ffff00";
const TYPE_5X_COLOR = "#008b8b";
const TYPE_5X_FILL_COLOR = "#00ffff";
const PASSENGER_COLOR = "#00008b";
const PASSENGER_FILL_COLOR = "#0000ff";
const CARGO_COLOR = "#006400";
const CARGO_FILL_COLOR = "#90ee90";
const TANKER_COLOR = "#8b0000";
const TANKER_FILL_COLOR = "#ff0000";
const OTHER_COLOR = "#008b8b";
const OTHER_FILL_COLOR = "#00ffff";

// Additional Color for Detected Vessel
const DETECTED_AIS_VESSEL_COLOR = "#900C3F";
const DETECTED_AIS_VESSEL_FILL_COLOR = "#C70039";

const DETECTED_RADAR_TARGET_COLOR = "#044389";
const DETECTED_RADAR_TARGET_FILL_COLOR = "#64bdf4";

const DETECTED_TLL_AIS_COLOR = "#548870";
const DETECTED_TLL_AIS_FILL_COLOR = "#a6daae";

const DETECTED_OPTIC_TARGET_COLOR = "#ff6700";
const DETECTED_OPTIC_TARGET_FILL_COLOR = "#ff9248";

const DETECTED_RADAR_TARGET_MQTT_COLOR = "#832161";
const DETECTED_RADAR_TARGET_MQTT_FILL_COLOR = "#DA4167";

const TYPES: { [key: number]: ShipType } = {
  0: newShipType("Not available", WIG_COLOR, WIG_FILL_COLOR),
  20: newShipType(
    "Wing in ground (WIG), all ships of this type",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  21: newShipType(
    "Wing in ground (WIG), Hazardous category A",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  22: newShipType(
    "Wing in ground (WIG), Hazardous category B",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  23: newShipType(
    "Wing in ground (WIG), Hazardous category C",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  24: newShipType(
    "Wing in ground (WIG), Hazardous category D",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  25: newShipType(
    "Wing in ground (WIG), Reserved for future use",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  26: newShipType(
    "Wing in ground (WIG), Reserved for future use",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  27: newShipType(
    "Wing in ground (WIG), Reserved for future use",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  28: newShipType(
    "Wing in ground (WIG), Reserved for future use",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  29: newShipType(
    "Wing in ground (WIG), Reserved for future use",
    WIG_COLOR,
    WIG_FILL_COLOR,
  ),
  30: newShipType("Fishing", TYPE_3X_COLOR, TYPE_3X_FILL_COLOR),
  31: newShipType("Towing", TYPE_3X_COLOR, TYPE_3X_FILL_COLOR),
  32: newShipType(
    "Towing: length exceeds 200m or breadth exceeds 25m",
    TYPE_3X_COLOR,
    TYPE_3X_FILL_COLOR,
  ),
  33: newShipType(
    "Dredging or underwater ops",
    TYPE_3X_COLOR,
    TYPE_3X_FILL_COLOR,
  ),
  34: newShipType("Diving ops", TYPE_3X_COLOR, TYPE_3X_FILL_COLOR),
  35: newShipType("Military ops", TYPE_3X_COLOR, TYPE_3X_FILL_COLOR),
  36: newShipType("Sailing", TYPE_3X_COLOR, TYPE_3X_FILL_COLOR),
  37: newShipType("Pleasure Craft", TYPE_3X_COLOR, TYPE_3X_FILL_COLOR),
  40: newShipType(
    "High speed craft (HSC), all ships of this type",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  41: newShipType(
    "High speed craft (HSC), Hazardous category A",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  42: newShipType(
    "High speed craft (HSC), Hazardous category B",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  43: newShipType(
    "High speed craft (HSC), Hazardous category C",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  44: newShipType(
    "High speed craft (HSC), Hazardous category D",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  45: newShipType(
    "High speed craft (HSC), Reserved for future use",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  46: newShipType(
    "High speed craft (HSC), Reserved for future use",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  47: newShipType(
    "High speed craft (HSC), Reserved for future use",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  48: newShipType(
    "High speed craft (HSC), Reserved for future use",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  49: newShipType(
    "High speed craft (HSC), No additional information",
    HSC_COLOR,
    HSC_FILL_COLOR,
  ),
  50: newShipType("Pilot Vessel", TYPE_5X_COLOR, TYPE_5X_FILL_COLOR),
  51: newShipType(
    "Search and Rescue vessel",
    TYPE_5X_COLOR,
    TYPE_5X_FILL_COLOR,
  ),
  52: newShipType("Tug", TYPE_5X_COLOR, TYPE_5X_FILL_COLOR),
  53: newShipType("Port Tender", TYPE_5X_COLOR, TYPE_5X_FILL_COLOR),
  54: newShipType(
    "Anti-pollution equipment",
    TYPE_5X_COLOR,
    TYPE_5X_FILL_COLOR,
  ),
  55: newShipType("Law Enforcement", TYPE_5X_COLOR, TYPE_5X_FILL_COLOR),
  56: newShipType("Spare - Local Vessel", TYPE_5X_COLOR, TYPE_5X_FILL_COLOR),
  57: newShipType("Spare - Local Vessel", TYPE_5X_COLOR, TYPE_5X_FILL_COLOR),
  58: newShipType("Medical Transport", TYPE_5X_COLOR, TYPE_5X_FILL_COLOR),
  59: newShipType(
    "Noncombatant ship according to RR Resolution No. 18",
    "",
    "",
  ),
  60: newShipType(
    "Passenger, all ships of this type",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  61: newShipType(
    "Passenger, Hazardous category A",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  62: newShipType(
    "Passenger, Hazardous category B",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  63: newShipType(
    "Passenger, Hazardous category C",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  64: newShipType(
    "Passenger, Hazardous category D",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  65: newShipType(
    "Passenger, Reserved for future use",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  66: newShipType(
    "Passenger, Reserved for future use",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  67: newShipType(
    "Passenger, Reserved for future use",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  68: newShipType(
    "Passenger, Reserved for future use",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  69: newShipType(
    "Passenger, No additional information",
    PASSENGER_COLOR,
    PASSENGER_FILL_COLOR,
  ),
  70: newShipType(
    "Cargo, all ships of this type",
    CARGO_COLOR,
    CARGO_FILL_COLOR,
  ),
  71: newShipType("Cargo, Hazardous category A", CARGO_COLOR, CARGO_FILL_COLOR),
  72: newShipType("Cargo, Hazardous category B", CARGO_COLOR, CARGO_FILL_COLOR),
  73: newShipType("Cargo, Hazardous category C", CARGO_COLOR, CARGO_FILL_COLOR),
  74: newShipType("Cargo, Hazardous category D", CARGO_COLOR, CARGO_FILL_COLOR),
  75: newShipType(
    "Cargo, Reserved for future use",
    CARGO_COLOR,
    CARGO_FILL_COLOR,
  ),
  76: newShipType(
    "Cargo, Reserved for future use",
    CARGO_COLOR,
    CARGO_FILL_COLOR,
  ),
  77: newShipType(
    "Cargo, Reserved for future use",
    CARGO_COLOR,
    CARGO_FILL_COLOR,
  ),
  78: newShipType(
    "Cargo, Reserved for future use",
    CARGO_COLOR,
    CARGO_FILL_COLOR,
  ),
  79: newShipType(
    "Cargo, No additional information",
    CARGO_COLOR,
    CARGO_FILL_COLOR,
  ),
  80: newShipType(
    "Tanker, all ships of this type",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  81: newShipType(
    "Tanker, Hazardous category A",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  82: newShipType(
    "Tanker, Hazardous category B",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  83: newShipType(
    "Tanker, Hazardous category C",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  84: newShipType(
    "Tanker, Hazardous category D",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  85: newShipType(
    "Tanker, Reserved for future use",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  86: newShipType(
    "Tanker, Reserved for future use",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  87: newShipType(
    "Tanker, Reserved for future use",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  88: newShipType(
    "Tanker, Reserved for future use",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  89: newShipType(
    "Tanker, No additional information",
    TANKER_COLOR,
    TANKER_FILL_COLOR,
  ),
  90: newShipType(
    "Other Type, all ships of this type",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  91: newShipType(
    "Other Type, Hazardous category A",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  92: newShipType(
    "Other Type, Hazardous category B",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  93: newShipType(
    "Other Type, Hazardous category C",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  94: newShipType(
    "Other Type, Hazardous category D",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  95: newShipType(
    "Other Type, Reserved for future use",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  96: newShipType(
    "Other Type, Reserved for future use",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  97: newShipType(
    "Other Type, Reserved for future use",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  98: newShipType(
    "Other Type, Reserved for future use",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  99: newShipType(
    "Other Type, no additional information",
    OTHER_COLOR,
    OTHER_FILL_COLOR,
  ),
  100: newShipType(
    "Detected AIS Vessel, no additional information",
    DETECTED_AIS_VESSEL_COLOR,
    DETECTED_AIS_VESSEL_FILL_COLOR,
  ),
  101: newShipType(
    "Detected RADAR Targets, no additional information",
    DETECTED_RADAR_TARGET_COLOR,
    DETECTED_RADAR_TARGET_FILL_COLOR,
  ),
  102: newShipType(
    "Detected OPTIC Targets, no additional information",
    DETECTED_OPTIC_TARGET_COLOR,
    DETECTED_OPTIC_TARGET_FILL_COLOR,
  ),
  103: newShipType(
    "Detected OPTIC Targets, no additional information",
    DETECTED_TLL_AIS_COLOR,
    DETECTED_TLL_AIS_FILL_COLOR,
  ),
  104: newShipType(
    "Detected RADAR Targets, no additional information",
    DETECTED_RADAR_TARGET_MQTT_COLOR,
    DETECTED_RADAR_TARGET_MQTT_FILL_COLOR,
  ),
};

const RESERVED_TYPE = newShipType(
  "Reserved",
  RESERVED_COLOR,
  RESERVED_FILL_COLOR,
);
const UNKNOWN_TYPE = newShipType(
  "Unknown",
  RESERVED_COLOR,
  RESERVED_FILL_COLOR,
);

const setExtraMarkerColor = (entity: INgsiAIS, marker: any) => {
  if (entity.shipType === 100) {
    marker.setStyle({
      color: TYPES[100].color,
      fill: true,
      fillOpacity: 1.0,
      fillColor: TYPES[100].fillColor,
    });
  }

  if (entity.shipType === 101) {
    marker.setStyle({
      color: TYPES[101].color,
      fill: true,
      fillOpacity: 1.0,
      fillColor: TYPES[101].fillColor,
    });
  }

  if (entity.shipType === 102) {
    marker.setStyle({
      color: TYPES[102].color,
      fill: true,
      fillOpacity: 1.0,
      fillColor: TYPES[102].fillColor,
    });
  }

  if (entity.shipType === 103) {
    marker.setStyle({
      color: TYPES[103].color,
      fill: true,
      fillOpacity: 1.0,
      fillColor: TYPES[103].fillColor,
    });
  }

  if (entity.shipType === 104) {
    marker.setStyle({
      color: TYPES[104].color,
      fill: true,
      fillOpacity: 1.0,
      fillColor: TYPES[104].fillColor,
    });
  }

  return marker;
};

export const createMarker = (
  entityId: string,
  instance: ILeafletInstance,
): TrackerDetails | undefined => {
  const entity = instance.entities.get(entityId) as INgsiAIS | undefined;
  const tempEntity = instance.additionalEntities.get(entityId) as
    | INgsiAIS
    | undefined;

  const aisEntity = entity ?? tempEntity;

  if (!aisEntity) {
    return;
  }

  const positionReport = setAISPositionReport(aisEntity);
  const options = setAISReportOptions(aisEntity);
  let marker = new (L as any).aisTrackSymbol(positionReport, options);

  marker = setExtraMarkerColor(aisEntity, marker);

  return {
    marker: marker,
    positionReport: positionReport,
    options: options,
  };
};

export const updatePositionReport = (
  entity: INgsiAIS,
  marker: any,
  instance: ILeafletInstance,
): TrackerDetails | undefined => {
  const tempEntity = instance.additionalEntities.get(entity.id) as
    | INgsiAIS
    | undefined;

  const positionReport = setAISPositionReport(entity);
  const options = setAISReportOptions(entity);

  marker.setPositionReport(positionReport, options);

  marker = setExtraMarkerColor(entity, marker);

  if (tempEntity) {
    instance.additionalEntities.set(entity.id, entity);
  } else {
    instance.entities.set(entity.id, entity);
  }

  instance.markers.set(entity.id, marker);

  return {
    marker: marker,
    positionReport: positionReport,
    options: options,
  };
};

export const updateMarkerHeading = (
  entity: INgsiAIS,
  marker: any,
  instance: ILeafletInstance,
): TrackerDetails | undefined => {
  const prevLatLng = marker.getLatLng();
  const storedEntity = instance.entities.get(entity.id) as INgsiAIS | undefined;
  const tempEntity = instance.additionalEntities.get(entity.id) as
    | INgsiAIS
    | undefined;

  const aisEntity = storedEntity ?? tempEntity;

  if (!aisEntity) {
    return;
  }

  if (!entity && !headingOrCourseChanged(entity, aisEntity)) {
    return;
  }

  if (entity?.cog) {
    aisEntity.cog = Number(entity.cog.toFixed(1));
  }

  if (entity?.hdg) {
    aisEntity.hdg = Number(entity.hdg.toFixed(1));
  }

  if (entity.windSpeedM) {
    aisEntity.windSpeedM = Number(entity.windSpeedM.toFixed(1));
  }

  if (prevLatLng) {
    aisEntity.location.coordinates[1] = prevLatLng.lat;
    aisEntity.location.coordinates[0] = prevLatLng.lng;
  }

  const positionReport = setAISPositionReport(aisEntity);
  const options = setAISReportOptions(aisEntity);

  marker.setPositionReport(positionReport, options);

  marker = setExtraMarkerColor(entity, marker);

  if (tempEntity) {
    instance.additionalEntities.set(entity.id, aisEntity);
  } else {
    instance.entities.set(entity.id, aisEntity);
  }

  instance.markers.set(entity.id, marker);

  return {
    marker: marker,
    positionReport: positionReport,
    options: options,
  };
};

export const setAISPositionReport = (entity: INgsiAIS) => {
  let heading = entity?.hdg ? entity.hdg : (entity?.cog ?? 0);
  let course = entity?.cog ?? 0;
  let speed = entity?.sog ?? 0;

  const positionReport: PositionReport = {
    latitude: entity.location.coordinates[1],
    longitude: entity.location.coordinates[0],
    trueHeading: Number(heading.toFixed(1)),
    cog: Number(course.toFixed(1)),
    sog: speed,
  };

  if (entity?.rot) {
    positionReport.rateOfTurn = entity.rot;
  }

  if (entity?.navigationStatus) {
    positionReport.navigationalStatus = entity.navigationStatus;
  }

  return positionReport;
};

export const setAISReportOptions = (entity: INgsiAIS) => {
  let options: { shipStaticData: ShipStaticData } = {
    shipStaticData: {
      name: entity.name,
    },
  };

  if (entity?.shipType) {
    options.shipStaticData.type = entity.shipType;
  }

  if (entity?.senderMmsi) {
    options.shipStaticData.userId = Number(entity.senderMmsi);
  }

  if (entity?.shipId) {
    options.shipStaticData.imoNumber = entity.shipId;
  }

  if (
    entity?.dimensionToBow &&
    entity?.dimensionToStern &&
    entity?.dimensionToPort &&
    entity?.dimensionToStarboard
  ) {
    options.shipStaticData.dimension = {
      A: entity.dimensionToBow,
      B: entity.dimensionToStern,
      C: entity.dimensionToPort,
      D: entity.dimensionToStarboard,
    };
  }

  if (entity?.destination) {
    options.shipStaticData.destination = entity.destination;
  }

  if (entity?.eta) {
    options.shipStaticData.eta = {
      month: new Date(entity?.dateObserved["@value"]).getMonth() + 1,
      day: new Date(entity?.dateObserved["@value"]).getDate(),
      hour: new Date(entity?.dateObserved["@value"]).getHours(),
      minute: new Date(entity?.dateObserved["@value"]).getMinutes(),
    };
  }

  if (entity?.callsign) {
    options.shipStaticData.callSign = entity.callsign;
  }

  if (entity?.draught) {
    options.shipStaticData.maximumStaticDraught = entity.draught;
  }

  if (entity?.fixType) {
    options.shipStaticData.fixType = entity.fixType;
  }

  if (entity?.dte) {
    options.shipStaticData.dte = entity.dte;
  }

  return options;
};

export const getAISCustomTrackPopupContent = (
  entity: any,
  translator: TranslateService,
  positionReport?: PositionReport,
  shipStaticData?: ShipStaticData,
): HTMLElement => {
  const content: string[] = ["<table>"];

  const addRow = (label: string, value: any, unit: string = ""): void => {
    content.push(createTableRow(label, value, unit));
  };

  entity.trackingId && addRow("TrackID", entity.trackingId);

  if (shipStaticData) {
    shipStaticData.name && addRow("Name", shipStaticData.name);
    shipStaticData.userId && addRow("MMSI", shipStaticData.userId);
    shipStaticData.imoNumber && addRow("IMO Number", shipStaticData.imoNumber);
    shipStaticData.callSign && addRow("Call sign", shipStaticData.callSign);
  }

  entity.observerMmsi && addRow("Observer MMSI", entity.observerMmsi);
  if (entity?.objectMmsi) {
    addRow("MMSI", entity.objectMmsi);
  }
  if (entity?.objTypeSource) {
    addRow("Object type source", entity.objTypeSource);
  }

  if (entity?.objType && Object.keys(entity.objType).length === 1) {
    addRow(
      "Object type",
      `${Object.keys(entity.objType)[0]} - ${Object.values(entity.objType)[0]}`,
    );
  }

  if (positionReport) {
    addRow(
      "Location",
      `${toFixed(positionReport.latitude, 5)}, ${toFixed(positionReport.longitude, 5)}`,
    );
    addRow(
      "SOG",
      toFixed(positionReport.sog, 2, (v) => v < MAX_SOG_EXCLUSIVE),
      "knots",
    );
    addRow("COG", toFixed(positionReport.cog, 1), "°");
    addRow("HDG", toFixed(positionReport.trueHeading, 1), "°");
    addRow("ROT", toFixed(positionReport.rateOfTurn, 1), "°/min");
    positionReport.navigationalStatus &&
      addRow(
        "Navigation status",
        toNavigationStatusString(positionReport.navigationalStatus),
      );
  }

  if (entity?.mtw) {
    addRow("TMP", toFixed(entity.mtw, 1), "°C");
  }

  if (entity?.depthMeters) {
    addRow("DBT", toFixed(entity.mtw, 1), "m");
  }

  if (entity?.windDirTrue) {
    addRow("TWD", toFixed(entity.windSpeedM, 1), "°");
  }

  if (entity?.windSpeedM) {
    addRow("TWS", toFixed(entity.windSpeedM, 1), "m/s");
  }

  if (shipStaticData) {
    shipStaticData.type && addRow("Type", toTypeString(shipStaticData.type));
    if (
      shipStaticData.dimension &&
      isDimensionValid(shipStaticData.dimension)
    ) {
      addRow(
        "Ship length",
        (shipStaticData.dimension.A + shipStaticData.dimension.B).toString(),
        "m",
      );
      addRow(
        "Ship width",
        (shipStaticData.dimension.C + shipStaticData.dimension.D).toString(),
        "m",
      );
    }
    shipStaticData.fixType &&
      addRow("Fix type", toFixTypeString(shipStaticData.fixType));
    shipStaticData.eta && addRow("ETA", toETAString(shipStaticData.eta));
    shipStaticData.maximumStaticDraught &&
      addRow(
        "Maximum static draught",
        toFixed(shipStaticData.maximumStaticDraught, 1),
        "m",
      );
    shipStaticData.destination &&
      addRow("Destination", shipStaticData.destination);
    shipStaticData.dte && addRow("DTE", shipStaticData.dte);
  }

  if (entity?.objLength && entity?.objWidth) {
    addRow("Object length", entity.objLength.toString(), "m");
    addRow("Object width", entity.objWidth.toString(), "m");
  }
  if (entity?.objDraught) {
    addRow("Draught", toFixed(entity?.objDraught, 1), "m");
  }

  entity.source &&
    entity.type !== NgsiAIS.entityType &&
    addRow("Source", entity.source);

  entity.dateObserved &&
    addRow(
      "Date observed",
      new Date(entity.dateObserved["@value"]).toLocaleString("de-DE", {
        timeZone: "Europe/Berlin",
      }),
    );

  content.push("</table>");
  const popupContent = DomUtil.create("div");
  popupContent.innerHTML = content.join("");
  return popupContent;
};

function headingOrCourseChanged(
  entity: INgsiAIS,
  storedEntity: INgsiAIS,
): boolean {
  if (entity.cog && entity.hdg) {
    return (
      courseHasChanged(entity.cog, storedEntity) ||
      headingHasChanged(entity.hdg, storedEntity)
    );
  }

  if (entity.hdg) {
    return headingHasChanged(entity.hdg, storedEntity);
  }

  if (entity.cog) {
    return courseHasChanged(entity.cog, storedEntity);
  }

  return false;
}

function courseHasChanged(cog: number, markerEntity: INgsiAIS): boolean {
  return Number(cog.toFixed(1)) !== markerEntity.cog;
}

function headingHasChanged(hdg: number, markerEntity: INgsiAIS): boolean {
  return Number(hdg.toFixed(1)) !== markerEntity.hdg;
}

function toFixed(
  v: number | null | undefined,
  fractionDigits?: number,
  isValid?: (v: number) => boolean,
): string | undefined {
  if (v === null || v === undefined) {
    return undefined;
  }
  if (isValid && !isValid(v)) {
    return undefined;
  }
  return v.toFixed(fractionDigits);
}

function newShipType(name: string, color: string, fillColor: string): ShipType {
  return {
    name: name,
    color: color,
    fillColor: fillColor,
  };
}

function toTypeString(type: number | null | undefined): string | undefined {
  if (type === null || type === undefined) {
    return undefined;
  }
  const shipType = getShipType(type);
  return shipType.name;
}

function toFixTypeString(
  fixType: number | null | undefined,
): string | undefined {
  if (fixType === null || fixType === undefined) {
    return undefined;
  }
  switch (fixType) {
    case 0:
      return undefined;
    case 1:
      return "GPS";
    case 2:
      return "GLONASS";
    case 3:
      return "combined GPS/GLONASS";
    case 4:
      return "Loran-C";
    case 5:
      return "Chayka";
    case 6:
      return "integrated navigation system";
    case 7:
      return "surveyed";
    case 8:
      return "Galileo";
    case 9:
    case 10:
    case 11:
    case 12:
    case 13:
    case 14:
      return `not used (${fixType})`;
    case 15:
      return "internal GNSS";
    default:
      return `unknown (${fixType})`;
  }
}

function toNavigationStatusString(
  navigationStatus: number | null | undefined,
): string | undefined {
  if (navigationStatus === null || navigationStatus === undefined) {
    return undefined;
  }
  switch (navigationStatus) {
    case 0:
      return "Under way using engine";
    case 1:
      return "At anchor";
    case 2:
      return "Not under command";
    case 3:
      return "Restricted manoeuverability";
    case 4:
      return "Constrained by her draught";
    case 5:
      return "Moored";
    case 6:
      return "Aground";
    case 7:
      return "Engaged in Fishing";
    case 8:
      return "Under way sailing";
    case 9:
      return "Reserved for future amendment of Navigational Status for HSC";
    case 10:
      return "Reserved for future amendment of Navigational Status for WIG";
    case 11:
      return "Reserved for future use";
    case 12:
      return "Reserved for future use";
    case 13:
      return "Reserved for future use";
    case 14:
      return "AIS-SART is active";
    case 15:
      return "Not defined (default)";
    default:
      return `unknown (${navigationStatus})`;
  }
}

function createTableRow(name: string, value: any, unit?: string): string {
  if (value === null || value === undefined) {
    return "";
  }
  const sValue = String(value);
  return `<tr><td>${name}</td><td>${sValue} ${isNullOrUndefined(unit) ? "" : unit}</td></tr>`;
}

function isNullOrUndefined(v: any): boolean {
  return v === null || v === undefined;
}

function isDimensionValid(dimension: Dimension | null | undefined): boolean {
  return (
    dimension !== null &&
    dimension !== undefined &&
    dimension.A > 0 &&
    dimension.B > 0 &&
    dimension.C > 0 &&
    dimension.D > 0
  );
}

function getShipType(type: number | null | undefined): ShipType {
  if (type === null || type === undefined) {
    return TYPES[0];
  }
  if (type < 0 || type > 102) {
    return UNKNOWN_TYPE;
  }
  const shipType = TYPES[type];
  if (!isNullOrUndefined(shipType)) {
    return shipType;
  }
  return RESERVED_TYPE;
}

function toETAString(eta: ETA | null | undefined): string | undefined {
  if (eta === null || eta === undefined) {
    return undefined;
  }
  const parts: string[] = [];
  if (!isNullOrUndefined(eta.month) && !isNullOrUndefined(eta.day)) {
    parts.push(
      `${eta.month.toString().padStart(2, "0")}/${eta.day.toString().padStart(2, "0")}`,
    );
  }
  if (!isNullOrUndefined(eta.hour) && !isNullOrUndefined(eta.minute)) {
    parts.push(
      `${eta.hour.toString().padStart(2, "0")}:${eta.minute.toString().padStart(2, "0")}`,
    );
  }
  if (parts.length === 0) {
    return undefined;
  }
  return `${parts.join(" ")} UTC`;
}
